/**
 * Metro map: lines of stations, drawn the way a transit map is.
 *
 * The root is the network's name. Each branch is a **line**; everything under a line, read in
 * order, is its **stations**. A station whose name appears on two lines is one **interchange** —
 * the lines meet there — which is the whole trick of the structure: write "Centro" under the red
 * line and under the blue line, and the two lines cross at Centro.
 *
 * Stations sit on an octilinear grid: every stretch of line runs across, up, or at 45 degrees.
 * The first line runs straight across. Each line after it starts from the stations it shares with
 * the lines already drawn: between two of them its stations are spread along a path with at most
 * one bend; before the first and after the last it strikes out in whichever of the eight
 * directions is freest. A line that shares nothing starts a row of its own below the rest.
 *
 * A station node's position is its **dot**: `(x, y)` is the top-left of a `METRO_DOT` square
 * centred on it. The scene hangs the name beside the dot, on whichever side the lines leave free,
 * so a name never sits on a line — and a station somebody dragged takes its lines with it.
 *
 * A line's own node is its badge, placed just before its first station. A pinned station stays
 * where it was put, the lines bend through it, and every other station keeps its place; the
 * root never moves.
 */
import { childrenOf, toPlainText } from '@orbit-mind/domain';
import { onlyMoves } from './moves';
export const METRO_LAYOUT_ID = 'metro';
/** The square a station's position describes; the dot is drawn at its centre. */
export const METRO_DOT = 14;
/** From a line's first station to the edge of its badge. */
export const METRO_BADGE_GAP = 18;
const MIN_HOP = 84;
/** Roughly how wide a character of a station's name is, and the widest a name wraps to. */
const NAME_GLYPH = 7.4;
const MAX_NAME = 220;
const MAX_HOP = 150;
/** The eight directions a line may take, across first. */
const DIRECTIONS = [
    { x: 1, y: 0 },
    { x: 0, y: 1 },
    { x: 1, y: 1 },
    { x: 1, y: -1 },
    { x: 0, y: -1 },
    { x: -1, y: 1 },
    { x: -1, y: 0 },
    { x: -1, y: -1 },
];
/**
 * What makes two stations the same station: the name, ignoring case, accents and spacing.
 *
 * A station with no name is only ever itself.
 */
export function stationKey(node) {
    const name = toPlainText(node.richText)
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
    return name === '' ? `#${node.id}` : name;
}
/**
 * The lines of a metro map, and the stations of each.
 *
 * Shared with the scene, so the layout and the drawing agree about which node is a station of
 * which line. Collapsed lines have no stations to draw; hidden nodes are not stations.
 */
export function metroLines(document) {
    const root = document.nodes[document.map.rootNodeId];
    if (root === undefined || root.collapsed)
        return [];
    const children = (node) => node.collapsed ? [] : childrenOf(document, node.id).filter((child) => !child.hidden);
    return children(root).map((line) => {
        const stations = [];
        const walk = (node) => {
            for (const child of children(node)) {
                stations.push(child);
                walk(child);
            }
        };
        walk(line);
        return { line, stations };
    });
}
/** Where a station's dot is, from its node. */
export function stationDot(node) {
    return { x: node.x + METRO_DOT / 2, y: node.y + METRO_DOT / 2 };
}
function distanceToSegment(point, a, b) {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const length = dx * dx + dy * dy;
    const t = length === 0
        ? 0
        : Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / length));
    return Math.hypot(point.x - (a.x + t * dx), point.y - (a.y + t * dy));
}
function unit(vector) {
    const length = Math.hypot(vector.x, vector.y);
    return length === 0 ? { x: 0, y: 0 } : { x: vector.x / length, y: vector.y / length };
}
/**
 * The points of a path from `a` to `b` with at most one bend: 45 degrees first, then straight.
 */
function octilinear(a, b) {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const diagonal = Math.min(Math.abs(dx), Math.abs(dy));
    const bend = { x: a.x + Math.sign(dx) * diagonal, y: a.y + Math.sign(dy) * diagonal };
    const same = (p, q) => Math.hypot(p.x - q.x, p.y - q.y) < 0.5;
    return same(bend, a) || same(bend, b) ? [a, b] : [a, bend, b];
}
/** The point a fraction `t` of the way along a polyline. */
function along(points, t) {
    const lengths = [];
    let total = 0;
    for (let index = 1; index < points.length; index += 1) {
        const a = points[index - 1];
        const b = points[index];
        if (a === undefined || b === undefined)
            continue;
        const length = Math.hypot(b.x - a.x, b.y - a.y);
        lengths.push(length);
        total += length;
    }
    let remaining = t * total;
    for (let index = 1; index < points.length; index += 1) {
        const a = points[index - 1];
        const b = points[index];
        const length = lengths[index - 1] ?? 0;
        if (a === undefined || b === undefined)
            continue;
        if (remaining <= length || index === points.length - 1) {
            const f = length === 0 ? 0 : Math.min(1, remaining / length);
            return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f };
        }
        remaining -= length;
    }
    return points[0] ?? { x: 0, y: 0 };
}
export function packMetro(input) {
    const { document } = input;
    const root = document.nodes[document.map.rootNodeId];
    if (root === undefined)
        return { positions: {} };
    const lines = metroLines(document);
    const size = (node) => input.extentOf(node.id);
    const pinned = (node) => node.pinned === true && input.preserveManualPositions;
    // Room for names: consecutive stations on a straight stretch alternate their names above and
    // below the line, so a name has two hops to fit in.
    //
    // Estimated from the length of the name rather than read from its extent. A station's extent is
    // its dot and its name together, and which side the name hangs on depends on where the station
    // ended up — spacing stations by it would move them, which would move the names, which would
    // change the extents: an arrangement that never settles.
    const widest = Math.max(0, ...lines.flatMap((entry) => entry.stations.map((station) => Math.min(MAX_NAME, toPlainText(station.richText).length * NAME_GLYPH))));
    const hop = Math.round(Math.max(MIN_HOP, Math.min(MAX_HOP, (widest + 20) / 2 + 12)));
    const dots = new Map();
    const drawn = [];
    const rootSize = size(root);
    const origin = { x: root.x + 150, y: root.y + rootSize.height + 96 };
    /** How crowded a run of stations from `from` in direction `d` would be. */
    const crowding = (from, direction, count, step) => {
        let score = 0;
        for (let index = 1; index <= count; index += 1) {
            const point = {
                x: from.x + direction.x * step * index,
                y: from.y + direction.y * step * index,
            };
            for (const dot of dots.values()) {
                if (Math.hypot(dot.x - point.x, dot.y - point.y) < hop * 0.7)
                    score += 10;
            }
            for (const [a, b] of drawn) {
                if (distanceToSegment(point, a, b) < hop * 0.35)
                    score += 4;
            }
        }
        return score;
    };
    /** The freest direction out of `from`, preferring to carry straight on along `heading`. */
    const freest = (from, heading, count) => {
        let best = DIRECTIONS[0] ?? { x: 1, y: 0 };
        let bestScore = Number.POSITIVE_INFINITY;
        DIRECTIONS.forEach((direction, rank) => {
            const step = direction.x !== 0 && direction.y !== 0 ? hop * 0.8 : hop;
            const turn = heading === null
                ? 0
                : Math.acos(Math.max(-1, Math.min(1, unit(direction).x * heading.x + unit(direction).y * heading.y)));
            // Turning back on itself is never an option; a gentle preference for going straight, and
            // for the directions listed first.
            const score = crowding(from, direction, count, step) + (turn > 2.5 ? 1000 : turn * 3) + rank * 0.1;
            if (score < bestScore) {
                bestScore = score;
                best = direction;
            }
        });
        return best;
    };
    const extend = (from, direction, count) => {
        const step = direction.x !== 0 && direction.y !== 0 ? hop * 0.8 : hop;
        return Array.from({ length: count }, (_, index) => ({
            x: from.x + direction.x * step * (index + 1),
            y: from.y + direction.y * step * (index + 1),
        }));
    };
    const lowest = () => {
        let y = Number.NEGATIVE_INFINITY;
        for (const dot of dots.values())
            y = Math.max(y, dot.y);
        return y;
    };
    for (const { stations } of lines) {
        const keys = [];
        for (const station of stations) {
            const key = stationKey(station);
            if (keys[keys.length - 1] !== key)
                keys.push(key);
        }
        if (keys.length === 0)
            continue;
        const anchors = keys.flatMap((key, index) => (dots.has(key) ? [index] : []));
        const set = (index, point) => {
            const key = keys[index];
            if (key !== undefined && !dots.has(key))
                dots.set(key, point);
        };
        if (anchors.length === 0) {
            // A line of its own: a new row under everything drawn so far.
            const y = dots.size === 0 ? origin.y : lowest() + hop * 1.4;
            keys.forEach((_, index) => {
                set(index, { x: origin.x + index * hop, y });
            });
        }
        else {
            // Between two stations already on the map, spread along a path with one bend.
            for (let k = 1; k < anchors.length; k += 1) {
                const a = anchors[k - 1] ?? 0;
                const b = anchors[k] ?? 0;
                const from = dots.get(keys[a] ?? '');
                const to = dots.get(keys[b] ?? '');
                if (from === undefined || to === undefined || b - a < 2)
                    continue;
                const path = Math.hypot(to.x - from.x, to.y - from.y) < 1
                    ? [from, { x: from.x, y: from.y + hop }, { x: from.x + hop, y: from.y + hop }, to]
                    : octilinear(from, to);
                for (let index = a + 1; index < b; index += 1)
                    set(index, along(path, (index - a) / (b - a)));
            }
            // Before the first shared station and after the last, the freest way out.
            const first = anchors[0] ?? 0;
            const last = anchors[anchors.length - 1] ?? 0;
            const lastDot = dots.get(keys[last] ?? '');
            if (lastDot !== undefined && last < keys.length - 1) {
                const previous = last > 0 ? dots.get(keys[last - 1] ?? '') : undefined;
                const heading = previous === undefined
                    ? null
                    : unit({ x: lastDot.x - previous.x, y: lastDot.y - previous.y });
                const direction = freest(lastDot, heading, keys.length - 1 - last);
                extend(lastDot, direction, keys.length - 1 - last).forEach((point, index) => {
                    set(last + 1 + index, point);
                });
            }
            const firstDot = dots.get(keys[first] ?? '');
            if (firstDot !== undefined && first > 0) {
                const next = dots.get(keys[first + 1] ?? '');
                const heading = next === undefined ? null : unit({ x: firstDot.x - next.x, y: firstDot.y - next.y });
                const direction = freest(firstDot, heading, first);
                extend(firstDot, direction, first).forEach((point, index) => {
                    set(first - 1 - index, point);
                });
            }
        }
        for (let index = 1; index < keys.length; index += 1) {
            const a = dots.get(keys[index - 1] ?? '');
            const b = dots.get(keys[index] ?? '');
            if (a !== undefined && b !== undefined)
                drawn.push([a, b]);
        }
    }
    /*
     * A station somebody placed by hand stays there, and only there: the rest of the network keeps
     * the places it would have anyway, and the line bends through the moved station. Letting a
     * pinned station anchor its line instead would drag the whole line after it — one station
     * moved, and the map rearranged under the finger. Its twins on other lines go where it went,
     * so an interchange never splits in two.
     */
    const placed = new Map();
    for (const { stations } of lines) {
        for (const station of stations) {
            const key = stationKey(station);
            if (pinned(station) && !placed.has(key))
                placed.set(key, stationDot(station));
        }
    }
    const dotOf = (station) => {
        const key = stationKey(station);
        return placed.get(key) ?? dots.get(key);
    };
    const placements = {};
    for (const { line, stations } of lines) {
        for (const station of stations) {
            const dot = dotOf(station);
            if (dot === undefined || pinned(station))
                continue;
            placements[station.id] = { x: dot.x - METRO_DOT / 2, y: dot.y - METRO_DOT / 2 };
        }
        // The badge, just before the first station, on the side away from the second.
        const first = stations[0];
        if (first === undefined || pinned(line))
            continue;
        const firstDot = dotOf(first);
        if (firstDot === undefined)
            continue;
        const second = stations.find((station) => stationKey(station) !== stationKey(first));
        const secondDot = second === undefined ? undefined : dotOf(second);
        const out = secondDot === undefined
            ? { x: -1, y: 0 }
            : unit({ x: firstDot.x - secondDot.x, y: firstDot.y - secondDot.y });
        const badge = size(line);
        const reach = METRO_BADGE_GAP + Math.abs(out.x) * (badge.width / 2) + Math.abs(out.y) * (badge.height / 2);
        placements[line.id] = {
            x: firstDot.x + out.x * reach - badge.width / 2,
            y: firstDot.y + out.y * reach - badge.height / 2,
        };
    }
    return { positions: onlyMoves(document, placements) };
}
export const metroLayout = {
    id: METRO_LAYOUT_ID,
    name: 'Metro map',
    run: packMetro,
};
//# sourceMappingURL=metro.js.map